import * as SystemJSLoader from 'systemjs';
import { Registry, ModuleResponse } from './registry';
import { NMP } from '@ipreo/nmp-types';
import {
  LOADER_TYPES,
  BUNDLE_TYPES,
  HTTP_CONFIG,
  LOG_PREFIXES,
  ERROR_MESSAGES,
  MODULE_FEDERATION,
  getModuleFederationSharedModules,
  type LoaderType,
} from './constants';

export interface NmpSystemJSLoader extends SystemJSLoader.System {
  _trickyDeps?: {
    bundles: any;
    packages: any;
    globals: any;
    map: any;
  };
}

let verboseLog: (manifest: NMP.ModuleManifest, text: string) => void;

export const DEFAULT_SYSTEM_CONFIG = {
  map: {
    css: 'https://us-east-1-public.prod.browsermodules.ihsmarkit.com/modules/systemjs-plugin-css/0.19.1/bundle.js',
  },
  meta: {
    '*.css': { loader: 'css' },
  },
};

function moduleId(moduleDef: { name: string; version: string }): string {
  return `${moduleDef.name}@${moduleDef.version}`;
}

function uniqueModuleDefs(moduleDefs: { name: string; version: string }[]) {
  const seen = new Set<string>();
  return moduleDefs.filter((mod) => {
    const id = moduleId(mod);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

function configureModule(
  manifest: NMP.ModuleManifest
): Promise<NMP.ModuleManifest> {
  return Promise.resolve(manifest)
    .then(configureBundle.bind(this))
    .then(loadDependencies.bind(this))
    .then(configureSystem.bind(this))
    .then(loadCSS.bind(this))
    .then(loadJS.bind(this))
    .then(loadSharedDependency.bind(this)); // Add this step to load shared deps
}

function loadDependencies(
  manifest: NMP.ModuleManifest
): Promise<NMP.ModuleManifest> {
  return Promise.all(
    (manifest.dependencies || []).map((dep) => configureModule.bind(this)(dep))
  ).then(() => manifest);
}

function configureBundle(
  manifest: NMP.ModuleManifest
): Promise<NMP.ModuleManifest> {
  if (manifest.bundledModules) {
    for (const [name, version] of Object.entries(manifest.bundledModules)) {
      const mod = { name, version };
      this.system._trickyDeps.bundles[name] = mod;
      this.system._trickyDeps.packages[name] = mod;
      this.system._trickyDeps.globals[name] = mod;
      this.system._trickyDeps.map[name] = mod;
    }
  }
  return Promise.resolve(manifest);
}

function configureSystem(
  manifest: NMP.ModuleManifest
): Promise<NMP.ModuleManifest> {
  const config = this.system.getConfig();

  // Bundle config
  if (manifest.loadConfig?.bundle?.length) {
    if (!this.system._trickyDeps.bundles[manifest.name]) {
      const bundlePath = this.system.resolveSync(manifest.jsBundle);
      config.bundles[bundlePath] = manifest.loadConfig.bundle;
      this.system.config({ bundles: config.bundles });
      this.system._trickyDeps.bundles[manifest.name] = manifest;
      verboseLog(manifest, 'CONFIG BUNDLE ADDED');
    }
  }
  // UMD map
  else if (manifest.jsBundle && manifest.jsBundleType === BUNDLE_TYPES.UMD) {
    if (
      !config.map[manifest.name] &&
      !this.system._trickyDeps.map[manifest.name]
    ) {
      config.map[manifest.name] = manifest.jsBundle;
      this.system.config({ map: config.map });
      this.system._trickyDeps.map[manifest.name] = manifest;
      verboseLog(manifest, 'CONFIG MAP ADDED');
    }
  }

  // Package config
  if (
    manifest.loadConfig?.package &&
    Object.keys(manifest.loadConfig.package).length
  ) {
    if (!this.system._trickyDeps.packages[manifest.name]) {
      config.packages[manifest.name] = manifest.loadConfig.package;
      this.system.config({ packages: config.packages });
      this.system._trickyDeps.packages[manifest.name] = manifest;
      verboseLog(manifest, 'CONFIG PACKAGE ADDED');
    }
  }

  return Promise.resolve(manifest);
}

function loadCSS(manifest: NMP.ModuleManifest): Promise<NMP.ModuleManifest> {
  if (manifest.cssBundle) {
    const path = this.system.resolveSync(manifest.cssBundle);
    if (!document.querySelector(`link[href*="${path}"]`)) {
      return this.system.import(path).then(() => manifest);
    }
  }
  return Promise.resolve(manifest);
}

function loadJS(manifest: NMP.ModuleManifest): Promise<NMP.ModuleManifest> {
  if (manifest.jsBundle && manifest.jsBundleType === BUNDLE_TYPES.GLOBAL) {
    if (!this.system._trickyDeps.globals[manifest.name]) {
      const path = this.system.resolveSync(manifest.jsBundle);
      if (!this.system.registry.get(path)) {
        this.system._trickyDeps.globals[manifest.name] = manifest;
        verboseLog(manifest, 'GLOBAL SCRIPT LOADING');
        return this.system.import(path).then(() => manifest);
      }
    }
  }
  return Promise.resolve(manifest);
}

function loadSharedDependency(
  manifest: NMP.ModuleManifest
): Promise<NMP.ModuleManifest> {
  // Load shared dependencies during the config chain so they're available for MF containers
  const shared = getModuleFederationSharedModules();
  if (shared.includes(manifest.name)) {
    if (manifest.jsBundleType === BUNDLE_TYPES.UMD) {
      // UMD shared dependencies can be loaded through SystemJS
      return this.system
        .import(manifest.name)
        .then(() => {
          return manifest;
        })
        .catch((error) => {
          console.warn(
            `${LOG_PREFIXES.NMP_LOADER} Failed to load UMD shared dependency ${manifest.name}:`,
            error
          );
          return manifest;
        });
    } else if (manifest.jsBundleType === BUNDLE_TYPES.MODULE_FEDERATION) {
      // Load Module Federation shared dependencies here to ensure proper order
      return this.load(manifest.jsBundle, {
        type: LOADER_TYPES.FEDERATION,
        exposedModule:
          manifest.exposedModule || MODULE_FEDERATION.DEFAULT_EXPOSED_MODULE,
        remoteName: manifest.remoteName || undefined,
        manifest: manifest,
      })
        .then((loadedModule) => {
          // Cache the actual module for share scope
          this.moduleCache.set(manifest.name, loadedModule);
          return manifest;
        })
        .catch((error) => {
          console.warn(
            `${LOG_PREFIXES.NMP_LOADER} Failed to load MF shared dependency ${manifest.name}:`,
            error
          );
          return manifest;
        });
    }
  }

  return Promise.resolve(manifest);
}
function loadModuleIntoSystem(
  manifest: NMP.ModuleManifest
): Promise<NMP.LoaderResponse> {
  if (manifest.jsBundleType === BUNDLE_TYPES.UMD) {
    return this.system
      .import(manifest.name)
      .then((module) => ({ module, manifest }));
  }

  if (manifest.jsBundleType === BUNDLE_TYPES.MODULE_FEDERATION) {
    // Check if this MF module was already loaded during dependency chain
    if (this.moduleCache.has(manifest.name)) {
      return Promise.resolve({
        module: this.moduleCache.get(manifest.name),
        manifest,
      });
    }

    // If not cached, load it now
    return this.load(manifest.jsBundle, {
      type: LOADER_TYPES.FEDERATION,
      exposedModule:
        manifest.exposedModule || MODULE_FEDERATION.DEFAULT_EXPOSED_MODULE,
      remoteName: manifest.remoteName || undefined,
      manifest: manifest,
    }).then((module) => {
      // Cache the loaded module
      this.moduleCache.set(manifest.name, module);
      return { module, manifest };
    });
  }

  return Promise.resolve({ module: null, manifest });
}

function processRegistryResponse(
  response: ModuleResponse
): Promise<NMP.LoaderResponse[]> {
  const meta = this.system.getConfig().meta || {};

  if (response.privateBaseURLs) {
    response.privateBaseURLs.forEach((url) => {
      meta[`${url}/modules/*`] = {
        authorization: HTTP_CONFIG.AUTHORIZATION,
        crossOrigin: HTTP_CONFIG.CROSS_ORIGIN,
      };
    });
  } else if (response.privateBaseURL) {
    meta[`${response.privateBaseURL}/modules/*`] = {
      authorization: HTTP_CONFIG.AUTHORIZATION,
      crossOrigin: HTTP_CONFIG.CROSS_ORIGIN,
    };
  }

  this.system.config({ meta });

  return Promise.all(
    response.manifests.map((m) => configureModule.bind(this)(m))
  ).then((manifests) =>
    Promise.all(
      manifests.map((m) =>
        loadModuleIntoSystem
          .bind(this)(m)
          .catch((err) => ({ error: err, module: null, manifest: m }))
      )
    )
  );
}

function correlateResults(
  requested: { name: string; version: string }[],
  results: NMP.LoaderResponse[]
): NMP.LoaderResponse[] {
  const map = new Map<string, NMP.LoaderResponse>();
  results.forEach((r) => map.set(moduleId(r.manifest), r));

  return requested.map((req) => {
    const found = map.get(moduleId(req));

    // If the module was not returned by the registry we create a valid LoaderResponse
    if (found) return found;

    return {
      manifest: req as any, // the original request becomes the manifest
      module: null, // required property
      error: ERROR_MESSAGES.MODULE_NOT_FOUND,
    } as NMP.LoaderResponse;
  });
}

export class Loader implements NMP.Loader {
  private registry: Registry;
  public system: NmpSystemJSLoader;
  public verbose = 'empty';
  private initializedContainers = new Set<string>(); // Track which containers we've initialized
  private containerModuleMap = new Map<string, string>(); // Track which container provides which module name
  private moduleCache = new Map<string, any>(); // Cache actual modules for share scope

  constructor(registryURL: string, namespace?: string) {
    this.system = namespace ? new SystemJS.constructor() : (SystemJS as any);

    if (!this.system._trickyDeps) {
      this.system._trickyDeps = {
        bundles: {},
        packages: {},
        globals: {},
        map: {},
      };
    }

    verboseLog = () => {
      // Verbose logging disabled to prevent exposing sensitive URLs
    };

    this.system.config(DEFAULT_SYSTEM_CONFIG);
    this.registry = new Registry(registryURL);
  }

  authenticate(token: string) {
    return this.registry.authenticate(token);
  }

  isLoaded(name: string): boolean {
    return this.system.has(name);
  }

  // ─────────────────────────────────────────────────────────────
  // Main public load() – supports both SystemJS and Module Federation
  // ─────────────────────────────────────────────────────────────
  async load(
    file: string,
    options?: {
      type?: LoaderType;
      exposedModule?: string;
      remoteName?: string;
      manifest?: NMP.ModuleManifest;
    }
  ): Promise<any> {
    const type = options?.type || LOADER_TYPES.SYSTEMJS;

    if (type === LOADER_TYPES.FEDERATION) {
      return this.loadRemoteContainer(
        file,
        options?.exposedModule || MODULE_FEDERATION.DEFAULT_EXPOSED_MODULE,
        options?.remoteName,
        options?.manifest
      );
    }

    return this.system.import(file);
  }

  // ─────────────────────────────────────────────────────────────
  // Robust Module Federation Loading
  // ─────────────────────────────────────────────────────────────
  private async loadRemoteContainer(
    remoteEntry: string,
    exposedModule: string,
    remoteName?: string,
    manifest?: NMP.ModuleManifest
  ): Promise<any> {
    // Step 1: Determine container name (explicit or detected)
    const containerName = remoteName || this.detectContainerByUrl(remoteEntry);

    // Step 2: Try to use existing container
    if (containerName) {
      const result = await this.tryLoadContainer(
        containerName,
        exposedModule,
        manifest
      );
      if (result) return result;
    }

    // Step 3: Load script and try again
    const before = this.getCurrentMfContainers();
    await this.loadScript(remoteEntry, remoteName);

    // Step 4: Detect container after loading (prefer original name, then detect, then diff)
    const finalContainerName =
      containerName ||
      this.detectContainerByUrl(remoteEntry) ||
      this.findNewContainer(before);

    if (finalContainerName) {
      return this.getContainerOrThrow(
        finalContainerName,
        exposedModule,
        manifest,
        remoteEntry
      );
    }

    // Last resort: any existing container
    const existing = this.getCurrentMfContainers();
    if (existing.size > 0) {
      const firstContainer = Array.from(existing)[0];
      return this.getContainerOrThrow(
        firstContainer,
        exposedModule,
        manifest,
        remoteEntry
      );
    }

    throw new Error(
      `Module Federation failed for ${remoteEntry}.\n` +
        `Please add "remoteName": "yourContainerName" to the module manifest.`
    );
  }

  private async tryLoadContainer(
    containerName: string,
    exposedModule: string,
    manifest?: NMP.ModuleManifest
  ): Promise<any | null> {
    const container = (window as any)[containerName];
    if (container?.get) {
      return this.initializeAndGetModule(
        container,
        containerName,
        exposedModule,
        manifest
      );
    }
    return null;
  }

  private async getContainerOrThrow(
    containerName: string,
    exposedModule: string,
    manifest: NMP.ModuleManifest | undefined,
    remoteEntry: string
  ): Promise<any> {
    const container = (window as any)[containerName];
    if (!container?.get) {
      throw new Error(
        `Remote container "${containerName}" not found on window after loading ${remoteEntry}`
      );
    }
    return this.initializeAndGetModule(
      container,
      containerName,
      exposedModule,
      manifest
    );
  }

  private getCurrentMfContainers(): Set<string> {
    return new Set(
      Object.keys(window).filter((key) => {
        const val = (window as any)[key];
        return (
          val && typeof val.init === 'function' && typeof val.get === 'function'
        );
      })
    );
  }

  private loadScript(src: string, expectedName?: string): Promise<void> {
    // Otherwise inject once and wait until the container actually has .get()
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (document.querySelector(`script[src="${src}"]`)) {
        // Script exists, wait a bit for container to be ready
        const check = (attempts = 0, maxAttempts = 100) => {
          // Add timeout: ~5s max
          if (this.isRemoteReady(expectedName)) {
            resolve();
          } else if (attempts < maxAttempts) {
            setTimeout(() => check(attempts + 1, maxAttempts), 50);
          } else {
            reject(
              new Error(
                `Timeout waiting for remote container after loading ${src}`
              )
            );
          }
        };
        check();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.type = 'text/javascript';

      script.onload = () => {
        const check = (attempts = 0, maxAttempts = 100) => {
          if (this.isRemoteReady(expectedName)) {
            resolve();
          } else if (attempts < maxAttempts) {
            setTimeout(() => check(attempts + 1, maxAttempts), 50);
          } else {
            reject(
              new Error(
                `Timeout waiting for remote container after loading ${src}`
              )
            );
          }
        };
        check();
      };

      script.onerror = () => reject(new Error(`Failed to load remote: ${src}`));

      document.head.appendChild(script);
    });
  }

  private isRemoteReady(expectedName?: string): boolean {
    // If expectedName provided, check specifically for it
    if (expectedName) {
      const container = (window as any)[expectedName];
      return container?.get && container?.init;
    }
    // Otherwise, find any container that is available
    return Object.keys(window).some((key) => {
      const container = (window as any)[key];
      return container?.get && container?.init;
    });
  }

  private detectContainerByUrl(remoteEntry: string): string | null {
    // Extract potential container name from URL patterns
    // Common patterns: bidcomp -> MuniBidcomp, deal-manager -> DealManager, etc.

    const url = new URL(remoteEntry, window.location.origin);
    const pathname = url.pathname;

    // Try to extract module name from path
    const pathSegments = pathname.split('/').filter(Boolean);
    const potentialNames = [];

    // Look for segments that might be module names
    for (const segment of pathSegments) {
      if (segment.includes('remoteEntry') || segment.includes('.js')) continue;

      // Convert kebab-case or snake_case to PascalCase with common prefixes
      const pascalCase = segment
        .split(/[-_]/)
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join('');

      // Common prefixes for this application
      potentialNames.push(`Muni${pascalCase}`);
      potentialNames.push(pascalCase);
      potentialNames.push(`${pascalCase}Container`);
    }

    // Check each potential name
    for (const name of potentialNames) {
      if ((window as any)[name]?.get && (window as any)[name]?.init) {
        return name;
      }
    }

    return null;
  }

  private markContainerAsInitialized(
    container: any,
    containerName: string
  ): any {
    this.initializedContainers.add(containerName);
    return container;
  }

  private async safeInitializeContainer(
    container: any,
    containerName: string,
    manifest?: NMP.ModuleManifest
  ): Promise<any> {
    if (this.initializedContainers.has(containerName)) {
      return container;
    }

    try {
      await container.init(this.createShareScope(manifest));
      return this.markContainerAsInitialized(container, containerName);
    } catch (error) {
      if (error.message && error.message.includes('already been initialized')) {
        return this.markContainerAsInitialized(container, containerName);
      }

      console.error(
        `${LOG_PREFIXES.NMP_LOADER} Container ${containerName} initialization failed:`,
        error
      );
      throw error;
    }
  }

  private async initializeAndGetModule(
    container: any,
    containerName: string,
    exposedModule: string,
    manifest?: NMP.ModuleManifest
  ): Promise<any> {
    await this.safeInitializeContainer(container, containerName, manifest);
    const factory = await container.get(exposedModule);
    if (!factory) {
      throw new Error(
        `Exposed module "${exposedModule}" not found in container "${containerName}"`
      );
    }
    const wrappedResult = this.wrapModuleResult(factory(), manifest);

    // Cache both the mapping and the actual module for share scope
    if (manifest?.name) {
      this.containerModuleMap.set(manifest.name, containerName);
      if (wrappedResult) {
        this.moduleCache.set(manifest.name, wrappedResult);
      }
    }

    return wrappedResult;
  }

  private wrapModuleResult(
    moduleResult: any,
    manifest?: NMP.ModuleManifest
  ): any {
    const platforms = manifest?.platforms as any;
    const moduleName = platforms?.angular?.moduleName;

    if (!moduleName || !moduleResult) {
      return moduleResult;
    }

    // Case 1: Module has moduleName as a direct property (already correctly structured)
    if (typeof moduleResult === 'object' && moduleName in moduleResult) {
      return moduleResult;
    }

    // Case 2: moduleResult is a factory function → call it
    let actualModule = moduleResult;
    if (typeof moduleResult === 'function') {
      try {
        actualModule = moduleResult();
      } catch (e) {
        // Return original moduleResult to allow further processing instead of null
        return moduleResult;
      }
    }

    // Case 3: After calling factory, check again with 'in' operator
    if (
      actualModule &&
      typeof actualModule === 'object' &&
      moduleName in actualModule
    ) {
      return actualModule;
    }

    // Case 4: Check for default export
    if (
      actualModule &&
      typeof actualModule === 'object' &&
      'default' in actualModule
    ) {
      return { [moduleName]: actualModule.default };
    }

    // Case 5: Wrap the module with the expected structure
    return { [moduleName]: actualModule };
  }

  private findNewContainer(previous: Set<string>): string | null {
    const current = this.getCurrentMfContainers();
    for (const name of current) {
      if (!previous.has(name)) {
        return name;
      }
    }
    return null;
  }

  private createShareScope(manifest?: NMP.ModuleManifest): any {
    const scope: any = {};
    const shared = getModuleFederationSharedModules();

    shared.forEach((moduleName) => {
      const getter = () => {
        // Priority 1: Check SystemJS registry first (for Angular core modules and UMD dependencies)
        if (this.system.has(moduleName)) {
          const module = this.system.get(moduleName);
          return Promise.resolve(() => module);
        }

        // Priority 2: Check for cached Module Federation modules
        if (this.moduleCache.has(moduleName)) {
          const module = this.moduleCache.get(moduleName);
          return Promise.resolve(() => module);
        }

        // If module not found, warn and reject
        console.warn(
          `${LOG_PREFIXES.NMP_LOADER} Shared module ${moduleName} not available - should have been loaded during dependency chain`
        );
        return Promise.reject(
          new Error(`Shared module ${moduleName} not available`)
        );
      };

      // Use version strategy based on module type and manifest info
      const version = this.getModuleVersion(moduleName, manifest);

      scope[moduleName] = {
        [version]: {
          get: getter,
          from: MODULE_FEDERATION.SHARE_SCOPE_FROM,
          eager: false,
          loaded: 0,
        },
      };
    });

    return scope;
  }

  private getModuleVersion(
    moduleName: string,
    manifest?: NMP.ModuleManifest
  ): string {
    // If we have manifest info and version is available, use it regardless of module name
    if (moduleName.startsWith('@ipreo/muni-')) {
      return manifest?.version || '*';
    }

    // If no version available, check if module is Angular and use specific version
    if (moduleName.startsWith('@angular/')) {
      return MODULE_FEDERATION.DEFAULT_VERSION; // 15.2.9
    }

    // Default to wildcard for everything else
    return '*';
  }

  register(name: string, module: any, override = false) {
    if (override || !this.system.has(name)) {
      this.system.set(name, this.system.newModule(module));
    }
  }

  // Clear initialization tracking (useful for debugging/testing)
  clearInitializationTracking() {
    this.initializedContainers.clear();
    this.containerModuleMap.clear();
    this.moduleCache.clear();
    console.log(
      `${LOG_PREFIXES.NMP_LOADER} Cleared container initialization tracking`
    );
  }

  loadModule(name: string, version: string): Promise<NMP.LoaderResponse> {
    return this.registry
      .fetchModule(name, version)
      .then((resp) => processRegistryResponse.bind(this)(resp))
      .then((results) => results[0]);
  }

  loadModules(
    modules: { name: string; version: string }[]
  ): Promise<NMP.LoaderResponse[]> {
    const unique = uniqueModuleDefs(modules);
    return this.registry
      .fetchModules(unique)
      .then((resp) => processRegistryResponse.bind(this)(resp))
      .then((results) => correlateResults(modules, results));
  }
}
